export default class WpMediaLibrary
{

    /**
     * requirements in PHP:
     * add_action('admin_enqueue_scripts', 'wp_enqueue_media');
     *
     * example usage HTML:
     *      <div class="js_image_library_wrapper">
     *          <label for="cover_image_button">Cover Bild</label> <br/>
     *          <span class="js_no_image_placeholder <?= isset($coverImagePost['url']) ? 'hidden' : '' ?>">
     *              Kein Bild ausgewählt
     *          </span>
     *          <img class="js_image_preview"
     *              src="<?= $coverImagePost['url'] ?? '' ?>"
     *              alt="<?= $coverImagePost['alt'] ?? '' ?>"
     *              title="<?= $coverImagePost['title'] ?? '' ?>"
     *              width="<?= $coverImagePost['width'] ?? '300' ?>"
     *          >
     *          <input type="button" class="js_select_image_button" value="Bild auswählen">
     *          <span style="margin-left: 10px;"><a href="#" class="js_remove_image_button">Bild entfernen</a></span>
     *          <input type="hidden" class="js_image_id" name="coverImageId" value="<?= $coverImageId ?>">
     *      </div>
     *
     *
     * example usage JavaScript:
     *      import {WpMediaLibrary} from 'plugins/xlib/js/xlib.js';
     *
     *      document.addEventListener('DOMContentLoaded', function() {
     *          WpMediaLibrary.bindImageLibraryPicker();
     *      });
     *
     */
    static bindImageLibraryPicker() {
        const mediaLibraryWrappers = document.querySelectorAll('.js_image_library_wrapper');

        mediaLibraryWrappers.forEach((mediaLibraryWrapper) => {
            const imageButton = mediaLibraryWrapper.querySelector('.js_select_image_button');
            const imageInput = mediaLibraryWrapper.querySelector('.js_image_id');
            const imagePreview = mediaLibraryWrapper.querySelector('.js_image_preview');
            const noImagePlaceholder = mediaLibraryWrapper.querySelector('.js_no_image_placeholder');
            const fileSize = mediaLibraryWrapper.querySelector('.js_file_size');

            if (!imageButton) {
                return;
            }

            const wpMediaLibrary = new WpMediaLibrary();

            imageButton.addEventListener('click', (event) => {
                event.preventDefault();

                wpMediaLibrary.mediaLibraryPicker({
                    title: 'Bild auswählen',
                    button: { text: 'Bild auswählen' },
                    fileType: 'image',
                    currentValue: imageInput.value ?? '',
                }, function(data) {
                    imageInput.value = data[0].id;
                    imagePreview.src = data[0].url;
                    imagePreview.style.maxWidth = '100%';
                    if (imagePreview) imagePreview.style.display = '';
                    if (noImagePlaceholder) noImagePlaceholder.classList.add('hidden');
                    if (fileSize) fileSize.innerHTML = data[0].file_size_string ?? '';
                });
            });

            // Find or create remove link (WordPress thumbnail style)
            let removeButton = mediaLibraryWrapper.querySelector('.js_remove_image_button');
            if (!removeButton) {
                removeButton = WpMediaLibrary._createRemoveButton(imageButton);
            }

            removeButton.addEventListener('click', (event) => {
                event.preventDefault();

                if (imageInput) imageInput.value = '';

                if (imagePreview) {
                    imagePreview.src = '';
                    imagePreview.style.display = 'none';
                }

                if (noImagePlaceholder) noImagePlaceholder.classList.remove('hidden');
            });
        });
    }

    /**
     * Unified media library picker for images, PDFs, and any WordPress media
     * Detects media type from data-media-type attribute
     *
     * Supports WordPress media type shorthands and multiple types:
     * @see https://developer.wordpress.org/reference/functions/wp_get_ext_types/
     *
     * Available shorthands: 'image', 'video', 'audio', 'document', 'spreadsheet',
     *                       'interactive', 'text', 'archive', 'code'
     *
     * HTML Examples:
     *
     * Single type (WordPress shorthand):
     * <input class="js_select_media_button"
     *        data-media-type="document"
     *        data-id="fileId"
     *        data-file="fileName"
     *        value="Datei auswählen">
     *
     * Multiple types (comma-separated):
     * <input class="js_select_media_button"
     *        data-media-type="document,image"
     *        data-id="fileId"
     *        value="Datei auswählen">
     *
     * Explicit MIME type:
     * <input class="js_select_media_button"
     *        data-media-type="application/pdf"
     *        data-id="pdfId"
     *        data-file="pdfFile"
     *        data-size="pdfSize"
     *        value="PDF auswählen">
     */
    static bindFileLibraryPicker() {
        const buttons = document.querySelectorAll('.js_select_media_button');

        buttons.forEach((button) => {
            const wpMediaLibrary = new WpMediaLibrary();
            const mediaType = button.dataset.mediaType;
            const multiple = button.dataset.multiple === 'true';

            button.addEventListener('click', (event) => {
                event.preventDefault();

                // Find container (for multiple items like PDF rows)
                const isMultiple = button.dataset.isMultiple === 'is-multiple';
                const container = isMultiple ? button.closest('.pdf-row') : button.parentElement;

                if (!container) {
                    console.error('Could not find container for media button');
                    return;
                }

                // Get current value
                const idField = button.dataset.id;
                const idInput = container.querySelector(`.${idField}`);
                const currentValue = idInput?.value || '';

                // Handle multiple media types (comma-separated or array)
                let fileType = mediaType;
                if (typeof mediaType === 'string' && mediaType.includes(',')) {
                    // Split comma-separated types into array
                    fileType = mediaType.split(',').map(t => t.trim());
                }

                // Open media library with correct type(s)
                wpMediaLibrary.mediaLibraryPicker({
                    currentValue: currentValue,
                    fileType: fileType,
                    multiple: multiple,
                }, function(files) {
                    if (files.length === 0) return;

                    const file = files[0];

                    // Update ID field
                    if (idInput) idInput.value = file.id;

                    // Handle media-type specific UI updates
                    // Check if fileType includes 'image' (handles both string and array)
                    const isImage = fileType === 'image' ||
                                    (Array.isArray(fileType) && fileType.includes('image'));

                    if (isImage) {
                        WpMediaLibrary._updateImageUI(container, button, file);
                    } else {
                        WpMediaLibrary._updateFileUI(container, button, file);
                    }
                });
            });
        });
    }

    /**
     * Helper: Create remove button with wrapper
     * @private
     */
    static _createRemoveButton(insertAfterElement) {
        const wrapper = document.createElement('span');
        wrapper.style.marginLeft = '10px';

        const removeButton = document.createElement('a');
        removeButton.href = '#';
        removeButton.className = 'js_remove_image_button';
        removeButton.textContent = 'Bild entfernen';

        wrapper.appendChild(removeButton);
        insertAfterElement.insertAdjacentElement('afterend', wrapper);

        return removeButton;
    }

    /**
     * Helper: Update image-specific UI
     * @private
     */
    static _updateImageUI(container, button, file) {
        const previewClass = button.dataset.preview;
        const preview = previewClass ? container.querySelector(`.${previewClass}`) : null;

        if (preview) {
            preview.src = file.url;
            preview.style.display = 'block';
            preview.style.maxWidth = '100%';
        }

        const placeholder = container.querySelector('.js_no_image_placeholder, .components-empty');
        if (placeholder) placeholder.classList.add('hidden');
    }

    /**
     * Helper: Update file (PDF/document) UI
     * @private
     */
    static _updateFileUI(container, button, file) {
        // Get field references from data attributes
        const fileField = button.dataset.file;
        const sizeField = button.dataset.size;
        const typeField = button.dataset.type;

        if (fileField) {
            const input = container.querySelector(`.${fileField}`);
            if (input) input.value = file.filename;
        }
        if (sizeField) {
            const input = container.querySelector(`.${sizeField}`);
            if (input) input.value = file.file_size_string || '';
        }
        if (typeField) {
            const input = container.querySelector(`.${typeField}`);
            if (input) input.value = file.subtype || '';
        }

        // Update UI display elements
        const fileInfo = container.querySelector('.file_info');
        const emptyPlaceholder = container.querySelector('.components-empty');
        const fileName = container.querySelector('.set__name b');
        const fileType = container.querySelector('.set__type');
        const fileSize = container.querySelector('.set__size');

        if (fileInfo) fileInfo.classList.remove('hidden');
        if (emptyPlaceholder) emptyPlaceholder.classList.add('hidden');
        if (fileName) fileName.textContent = file.filename;
        if (fileType) fileType.textContent = file.subtype || '';
        if (fileSize) fileSize.textContent = file.file_size_string || '';
    }

    /**
     * Core media library picker implementation
     *
     * Opens WordPress media library with specified options
     *
     * @param {Object} options - Configuration options
     * @param {string} options.title - Modal title (default: 'Medium auswählen')
     * @param {Object} options.button - Button configuration {text: 'Button text'}
     * @param {string|string[]} options.fileType - Media type filter (WordPress shorthand or MIME type)
     *                                             Examples: 'document', 'image', ['document','image']
     *                                             @see https://developer.wordpress.org/reference/functions/wp_get_ext_types/
     * @param {boolean} options.multiple - Allow multiple file selection (default: false)
     * @param {string} options.currentValue - Pre-selected attachment IDs (comma-separated)
     *
     * @param {Function} callback - Callback function receiving selected files
     *                              callback(files) where files is array of {id, filename, url, type, subtype, sizes, file_size_string}
     *
     * @example
     * // Single document type
     * mediaLibraryPicker({fileType: 'document'}, function(files) { console.log(files); });
     *
     * @example
     * // Multiple types
     * mediaLibraryPicker({fileType: ['document', 'image']}, function(files) { console.log(files); });
     */
    mediaLibraryPicker(options, callback) {
        options = (typeof options === 'object') ? options : {};

        // Predefined settings
        const defaultOptions = {
            title: 'Medium auswählen',
            button: {
                text: 'Auswählen'
            },
            fileType: null,
            multiple: false,
        };

        options = { ...defaultOptions, ...options };

        // Transform fileType to library.type for wp.media()
        const wpMediaOptions = {
            title: options.title,
            button: options.button,
            multiple: options.multiple,
        };

        // Add library filter if fileType specified
        if (options.fileType) {
            wpMediaOptions.library = {
                type: options.fileType
            };
        }

        // Check if WordPress media library is loaded
        if (typeof wp === 'undefined' || typeof wp.media === 'undefined') {
            console.error('WordPress media library not loaded. Ensure wp_enqueue_media() is called.');
            return;
        }

        // Create fresh media frame with current options (no caching)
        const mediaFrame = wp.media(wpMediaOptions);

        mediaFrame.on('open', () => {
            // On open, get the id from the hidden input
            // and select the appropiate images in the media manager
            const selection = mediaFrame.state().get('selection');
            const ids = (options.currentValue || '').split(',').filter(id => id.trim());

            ids.forEach((id) => {
                const attachment = wp.media.attachment(id);
                attachment.fetch();
                selection.add( attachment ? [ attachment ] : [] );
            });
        });

        mediaFrame.on('select', () => {
            // On close, get selections and save to the hidden input
            // plus other AJAX stuff to refresh the image preview
            const selection = mediaFrame.state().get('selection');
            const files = [];

            selection.each((attachment) => {
                files.push({
                    id: attachment.attributes.id,
                    filename: attachment.attributes.filename,
                    url: attachment.attributes.url,
                    type: attachment.attributes.type,
                    subtype: attachment.attributes.subtype,
                    sizes: attachment.attributes.sizes,
                    file_size_string: attachment.attributes.filesizeHumanReadable,
                });
            });

            callback(files);
        });

        mediaFrame.open();
    }
}

